import { useId } from 'react';
// 首页服务与保障带：产地直采 / 冷链可视 / 单证合规 / 履约保障（对齐官网叙事）
import { Box, Typography, SvgIcon } from '@mui/material';
import { Agriculture, Thermostat, Description, GppGood } from '@mui/icons-material';
import { usePrefs } from '@/store/BuyerPrefsContext';

import { tr } from '@/i18n';
/** Each corner motif has the distinct material treatment used in the reference. */
function GradientDecoration({ kind }: { kind: 'snowflake' | 'shield' | 'globe' }) {
  const gradientId = useId();
  const highlightId = useId();
  const paint = `url(#${gradientId})`;
  const shieldPath = 'M40 7C29 15 21 17 12 18V39C12 54 22 66 40 75C58 66 68 54 68 39V18C59 17 51 15 40 7Z';
  return <SvgIcon viewBox="0 0 80 80" className={`buyer-service-gradient-art buyer-service-art--${kind}`}>
    <defs>
      <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
        <stop stopColor="#8CE9C9" stopOpacity=".10" />
        <stop offset=".55" stopColor="#68DDB4" stopOpacity=".24" />
        <stop offset="1" stopColor="#BFF7E3" stopOpacity=".05" />
      </linearGradient>
      <radialGradient id={highlightId} cx="30%" cy="22%" r="80%">
        <stop stopColor="#D8FFF0" stopOpacity=".32" />
        <stop offset=".65" stopColor="#87E8C5" stopOpacity=".21" />
        <stop offset="1" stopColor="#54D8AA" stopOpacity=".10" />
      </radialGradient>
    </defs>
    {kind === 'snowflake' && <g fill="none" stroke="#83E5C4" strokeOpacity=".22" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
      {[0, 60, 120].map((angle) => <g key={angle} transform={`rotate(${angle} 40 40)`}>
        <path d="M40 8V72M30 17L40 26L50 17M30 63L40 54L50 63" />
      </g>)}
    </g>}
    {kind === 'shield' && <>
      <path d={shieldPath} transform="translate(13 12) scale(.90)" fill={paint} opacity=".38" />
      <path d={shieldPath} transform="translate(7 7) scale(.94)" fill={paint} opacity=".45" />
      <path d={shieldPath} fill={paint} />
      <path d="M27 40L36 49L53 31" fill="none" stroke="#FFFFFF" strokeOpacity=".85" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    </>}
    {kind === 'globe' && <>
      <circle cx="40" cy="40" r="32" fill={`url(#${highlightId})`} />
      <g fill="none" stroke="#FFFFFF" strokeOpacity=".72" strokeWidth="2.8">
        <circle cx="40" cy="40" r="32" />
        <ellipse cx="40" cy="40" rx="15" ry="32" />
        <path d="M8 40H72M13 23Q40 32 67 23M13 57Q40 48 67 57M40 8V72" />
      </g>
    </>}
  </SvgIcon>;
}

export default function ServiceBand() {
  const { t, lang } = usePrefs();
  const leafGradientId = useId();
  const leafVeinId = useId();
  const items = [
    { icon: <Agriculture />, decoration: <SvgIcon viewBox="0 0 80 80">
      <defs>
        <linearGradient id={leafGradientId} x1="0" y1="1" x2="1" y2="0"><stop offset="0" stopColor="#27C995" stopOpacity=".02" /><stop offset=".48" stopColor="#58DDB0" stopOpacity=".22" /><stop offset="1" stopColor="#B6F5DC" stopOpacity=".35" /></linearGradient>
        <linearGradient id={leafVeinId} x1="0" y1="1" x2="1" y2="0"><stop stopColor="#00AD7B" stopOpacity=".02" /><stop offset=".6" stopColor="#00AD7B" stopOpacity=".13" /><stop offset="1" stopColor="#00AD7B" stopOpacity="0" /></linearGradient>
      </defs>
      <path d="M8 78C7 42 32 14 77 7C74 49 56 79 8 78Z" fill={`url(#${leafGradientId})`} />
      <path d="M8 78C26 50 45 31 72 13C49 36 32 57 22 79Z" fill={`url(#${leafVeinId})`} />
    </SvgIcon>, title: t('service.s1t'), desc: t('service.s1d') },
    { icon: <Thermostat />, decoration: <GradientDecoration kind="snowflake" />, title: t('service.s2t'), desc: t('service.s2d') },
    { icon: <Description />, decoration: <GradientDecoration kind="shield" />, title: t('service.s3t'), desc: t('service.s3d') },
    { icon: <GppGood />, decoration: <GradientDecoration kind="globe" />, title: t('service.s4t'), desc: t('service.s4d') },
  ];

  return (
    <Box className="buyer-services">
      <Typography sx={{ fontSize: { xs: '21px', md: '28px' }, fontWeight: 700, color: '#212121', mb: 1.5 }}>{t('service.title')}</Typography>
      <Typography sx={{ color: '#8b939e', fontSize: { xs: 13, md: 16 }, mb: 2 }}>{tr('service.endToEndSupportFor')}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, md: 1.5 } }}>
        {items.map((it) => (
          <Box
            className="buyer-service-card"
            key={it.title}
            sx={{
              bgcolor: '#FFFFFF',
              border: '1px solid #F0F2F5',
              borderRadius: '14px',
              p: { xs: 1.5, md: 2 },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: '#ECFDF5',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
                '& .MuiSvgIcon-root': { fontSize: 20 },
              }}
            >
              {it.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#212121' }}>{it.title}</Typography>
            <Typography sx={{ fontSize: '11px', color: '#757575', mt: 0.25, lineHeight: 1.5 }}>{it.desc}</Typography></Box>
            <Box className="buyer-service-watermark" aria-hidden="true">{it.decoration}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
